import { useMediaQuery } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { getFeatureFlags, type FeatureFlagsResponse } from '@/api/experiments';
import {
  getFeatureFlagCookieScope,
  setOrRemoveFeatureFlagCookie,
  type FeatureFlagCookieClient,
  type FeatureFlagCookieScope,
} from '@/helpers/cookies';
import { isSSG } from '@/helpers/ssgUtils';
import { isIS24User } from '@/helpers/userUtils';
import { trackFeatureFlagsInRum } from '@/services/datadog/DatadogService';
import { useInsertionStore } from '@/stores/insertion';
import { FeatureFlagName } from '@/types';

/** See docs/AB_TESTING_GUIDELINES.md */
export const Treatment = {
  ON: 'on',
  OFF: 'off',
} as const;

export const variantName = (name: string) => `variant-${name}` as const;

interface FlagSetConfig {
  name: string;
  isEligible?: () => boolean;
}

/** See docs/AB_TESTING_GUIDELINES.md#flag-set-registry */
const FLAG_SETS: FlagSetConfig[] = [
  {
    name: 'mobile',
    isEligible: () => useMediaQuery('(max-width: 768px)').value,
  },
  { name: 'insertion_funnel' },
  { name: 'global' },
];

let fetchFlagSetsPromise: Promise<void> | null = null;

export const CUSTOM_FEATURE_FLAG_VALUES: FeatureFlagName[] = [
  FeatureFlagName.HOW,
  FeatureFlagName.REMOVE_INVOICE_FROM_BASIC,
  FeatureFlagName.NEW_CONSTRUCTION,
];

export enum SupportedFeatures {
  TEST_FEATURE = 'FeatureName',
  T2T_CROSSLISTING = 't2tCrosslisting',
  DD4035_NAVIGATION_EXPERIMENT = 'DD-4035-Alternate-Header-Navigation-Links',
  HOW = 'HOW',
  CONTACT_SEEKERS_UPGRADE_FDT = 'contactSeekerFdt',
  T2F_FOREVER_FREE = 't2fForeverFree',
  REMOVE_INVOICE_FROM_BASIC = 'removeInvoiceFromBasic',
  NEW_LANDING_PAGE = 'newLandingPage',
  VIEW_DEVELOPMENT_GRAPH_MY_ADS = 'viewDevelopmentGraphMyAds',
  NEW_CONSTRUCTION = 'newConstruction',
}

export function resetFeatureFlagFetchStateForTests(): void {
  fetchFlagSetsPromise = null;
}

async function processAllFlagSets(
  cookies: FeatureFlagCookieClient | undefined,
  scope: FeatureFlagCookieScope,
  onError: (error: Error) => void,
): Promise<void> {
  for (const set of FLAG_SETS) {
    if (set.isEligible && !set.isEligible()) continue;

    try {
      const response: FeatureFlagsResponse = await getFeatureFlags([set.name]);
      const treatments = response.data?.treatmentsWithConfig;

      Object.values(FeatureFlagName).forEach((flagName) => {
        if (!(flagName in (treatments ?? {}))) return;
        setOrRemoveFeatureFlagCookie(cookies, scope, flagName, treatments[flagName]?.treatment);
      });
      trackFeatureFlagsInRum(treatments);
    } catch (e) {
      onError(e instanceof Error ? e : new Error('Unknown error'));
    }
  }
}

export const useFeatureFlagsStore = defineStore('featureFlags', () => {
  const cookies = useCookies();
  const route = useRoute();
  const hasLoaded = ref(false);
  const error = ref<Error | null>(null);

  const featureFlags = computed(() => {
    return Object.values(FeatureFlagName)
      .filter((featureFlag) => featureFlag !== FeatureFlagName.TEST_NAME)
      .filter((featureFlag) => !CUSTOM_FEATURE_FLAG_VALUES.includes(featureFlag))
      .reduce<Record<FeatureFlagName, string | undefined>>(
        (result, flagName) => {
          const treatment = cookies?.get(flagName);
          if (typeof treatment === 'string' && treatment !== '') {
            result[flagName] = treatment;
          }
          return result;
        },
        {} as Record<FeatureFlagName, string | undefined>,
      );
  });

  function isFeatureEnabled(key: SupportedFeatures): boolean {
    const features = getFeatureQueryForFunnel();
    return features
      .split(',')
      .map((feature) => feature.trim())
      .includes(key);
  }

  /** Feature query string from current route to forward when navigating to insertion funnel. */
  function getFeatureQueryForFunnel(): string {
    const feature = route?.query?.feature;
    return typeof feature === 'string' ? feature : '';
  }

  function resolveTreatment(flag: FeatureFlagName): string | undefined {
    if (isSSG()) return undefined;

    const cookieValue = cookies?.get(flag);
    if (typeof cookieValue === 'string' && cookieValue !== '') {
      return cookieValue;
    }

    const insertionMetaFlags = useInsertionStore().insertion?.meta?.featureFlags;
    const metaValue = insertionMetaFlags?.[flag];
    if (typeof metaValue === 'string' && metaValue !== '') {
      return metaValue;
    }

    return undefined;
  }

  /**
   * Fetches all flag sets sequentially in array order. See docs/AB_TESTING_GUIDELINES.md
   */
  async function fetchFlagSets(): Promise<void> {
    if (isSSG()) return;
    if (hasLoaded.value) return;
    if (fetchFlagSetsPromise) {
      await fetchFlagSetsPromise;
      return;
    }

    fetchFlagSetsPromise = (async () => {
      try {
        const scope = getFeatureFlagCookieScope();
        await processAllFlagSets(cookies, scope, (flagSetError) => {
          error.value = flagSetError;
        });
      } catch (e) {
        error.value = e instanceof Error ? e : new Error('Unknown error');
      } finally {
        hasLoaded.value = true;
      }
    })();

    try {
      await fetchFlagSetsPromise;
    } finally {
      fetchFlagSetsPromise = null;
    }
  }

  function getTrackingValue(): string | undefined {
    const result = Object.values(FeatureFlagName).reduce<string[]>((result, featureFlag) => {
      const cookieValue = cookies?.get(featureFlag);
      if (cookieValue) {
        result.push(`${featureFlag}=${cookieValue}`);
      }
      return result;
    }, []);

    return result.length > 0 ? result.join('&') : undefined;
  }

  function getTreatment(flag: FeatureFlagName): string | undefined {
    return resolveTreatment(flag);
  }

  function isTreatmentOn(flag: FeatureFlagName): boolean {
    return resolveTreatment(flag) === Treatment.ON;
  }

  const isNewLandingPageTreatment = computed(() => {
    if (isIS24User()) {
      return false;
    }

    return isTreatmentOn(FeatureFlagName.NEW_LANDING_PAGE);
  });

  const isNewLandingPageVariantForDisplay = computed(
    () => hasLoaded.value && isNewLandingPageTreatment.value,
  );

  /**
   * The landing page experiment is Homegate-only, so the router never fetches flag sets for
   * anonymous IS24 landing visits. Gating rendering on `hasLoaded` there would wait forever.
   */
  const isExperimentResolved = computed(() => hasLoaded.value || isIS24User());

  function isQueryParamEnabled(feature: SupportedFeatures): boolean {
    return isFeatureEnabled(feature);
  }

  function getMergedFeatureFlags(): Record<string, string | undefined> {
    const baseFeatureFlags = featureFlags.value;
    const mergedFlags = { ...baseFeatureFlags };

    const customFlagMapping: Record<FeatureFlagName, SupportedFeatures | undefined> = {
      [FeatureFlagName.HOW]: SupportedFeatures.HOW,
      [FeatureFlagName.REMOVE_INVOICE_FROM_BASIC]: SupportedFeatures.REMOVE_INVOICE_FROM_BASIC,
      [FeatureFlagName.NEW_CONSTRUCTION]: SupportedFeatures.NEW_CONSTRUCTION,
    } as any;

    // Add custom feature flag values if they are enabled
    CUSTOM_FEATURE_FLAG_VALUES.forEach((customFlagName) => {
      const supportedFeature = customFlagMapping[customFlagName];
      if (supportedFeature && isFeatureEnabled(supportedFeature)) {
        mergedFlags[customFlagName] = 'on';
      } else {
        const treatmentValue = getTreatment(customFlagName);
        if (treatmentValue) {
          mergedFlags[customFlagName] = treatmentValue;
        }
      }
    });

    // HG-only experiment: never persist the flag onto an IS24 insertion
    if (isIS24User()) {
      delete mergedFlags[FeatureFlagName.NEW_LANDING_PAGE];
    }

    if (!isIS24User()) {
      delete mergedFlags[FeatureFlagName.REMOVE_INVOICE_FROM_BASIC];
    }

    return mergedFlags;
  }

  return {
    error,
    hasLoaded,
    featureFlags,
    isQueryParamEnabled,
    isTreatmentOn,
    isNewLandingPageTreatment,
    isNewLandingPageVariantForDisplay,
    isExperimentResolved,
    getTreatment,
    getTrackingValue,
    fetchFlagSets,
    isFeatureEnabled,
    getFeatureQueryForFunnel,
    getMergedFeatureFlags,
  };
});
